import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  clearAllRead,
} from '../services/notificationsApi';

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params?: any) => [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

/**
 * Hook to fetch notifications
 */
export const useNotifications = (params?: {
  unreadOnly?: boolean;
  type?: string;
  page?: number;
  limit?: number;
}) => {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => getNotifications(params),
  });
};

/**
 * Hook to get unread count
 */
export const useUnreadCount = () => {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: getUnreadCount,
    refetchInterval: 30000, // Poll every 30 seconds
  });
};

/**
 * Hook for notification mutations
 */
export const useNotificationMutations = () => {
  const queryClient = useQueryClient();

  const invalidateNotifications = () => {
    queryClient.invalidateQueries({ queryKey: notificationKeys.all });
  };

  const markAsReadMutation = useMutation({
    mutationFn: (id: string) => markAsRead(id),
    onSuccess: () => invalidateNotifications(),
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: () => markAllAsRead(),
    onSuccess: () => invalidateNotifications(),
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: () => invalidateNotifications(),
  });

  const clearAllReadMutation = useMutation({
    mutationFn: () => clearAllRead(),
    onSuccess: () => invalidateNotifications(),
  });

  return {
    markAsRead: markAsReadMutation,
    markAllAsRead: markAllAsReadMutation,
    deleteNotification: deleteNotificationMutation,
    clearAllRead: clearAllReadMutation,
    isLoading:
      markAsReadMutation.isPending ||
      markAllAsReadMutation.isPending ||
      deleteNotificationMutation.isPending ||
      clearAllReadMutation.isPending,
  };
};

export default useNotifications;
